import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Popover,
  PopoverProps,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as AutoIcon } from '@work4all/assets/icons/auto.svg';
import { ReactComponent as NoAutoIcon } from '@work4all/assets/icons/no-auto.svg';

import { BaseActionButton } from '@work4all/components/lib/input/base-action-button/BaseActionButton';
import { CheckboxRadioItem } from '@work4all/components/lib/input/checkbox-radio-item';

import { useErpPositionsAutomaticNumberingContext } from '../../../../../../contexts/ErpPositionsAutomaticNumberingContext';

export const NumberHeader = () => {
  const { t } = useTranslation();
  const [popoverSatte, setPopoverState] = useState<
    Pick<PopoverProps, 'open' | 'anchorEl'>
  >({
    open: false,
    anchorEl: null,
  });

  const {
    isRecordAutomaticNumberingEnabled,
    toggleRecordAutomaticNumbering,
    canEditAutomaticNumberingField,
    deletePositionsNumber,
  } = useErpPositionsAutomaticNumberingContext();

  const color = isRecordAutomaticNumberingEnabled
    ? 'var(--brand01)'
    : 'var(--text03)';

  return (
    <Box display="flex" alignItems="center">
      <NumberHeaderPopover
        {...popoverSatte}
        onClose={() => {
          setPopoverState({ open: false, anchorEl: null });
        }}
        isRecordAutomaticNumberingEnabled={isRecordAutomaticNumberingEnabled}
        toggleRecordAutomaticNumbering={toggleRecordAutomaticNumbering}
        deletePositionsNumber={deletePositionsNumber}
      />

      <Typography variant="caption">{t('COMMON.ERP.NUMBER')}</Typography>

      {canEditAutomaticNumberingField && (
        <BaseActionButton
          onClick={(e) => {
            setPopoverState({ open: true, anchorEl: e.currentTarget });
          }}
        >
          <Tooltip
            title={
              isRecordAutomaticNumberingEnabled
                ? t('COMMON.AUTOMATIC_NUMBERING_ACTIVE')
                : t('COMMON.AUTOMATIC_NUMBERING_INACTIVE')
            }
          >
            {isRecordAutomaticNumberingEnabled ? <AutoIcon /> : <NoAutoIcon />}
          </Tooltip>
        </BaseActionButton>
      )}
    </Box>
  );
};

type NumberHeaderDialogProps = Pick<
  PopoverProps,
  'onClose' | 'open' | 'anchorEl'
> & {
  isRecordAutomaticNumberingEnabled: boolean;
  toggleRecordAutomaticNumbering: () => void;
  deletePositionsNumber: () => void;
};

const NumberHeaderPopover = ({
  open,
  onClose,
  anchorEl,
  isRecordAutomaticNumberingEnabled,
  toggleRecordAutomaticNumbering,
  deletePositionsNumber,
}: NumberHeaderDialogProps) => {
  const { t } = useTranslation();

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: '8px',
          },
        },
      }}
    >
      <Box width={300}>
        <Box p="8px">
          <CheckboxRadioItem
            control="checkbox"
            label={t('COMMON.AUTOMATIC_NUMBERING')}
            caption={t('NUMBER_HEADER_POPOVER.CHECKBOX.CAPTION')}
            checked={isRecordAutomaticNumberingEnabled}
            onChange={toggleRecordAutomaticNumbering}
          />
        </Box>

        {isRecordAutomaticNumberingEnabled ? null : (
          <Box p="8px" borderTop={1} borderColor="var(--ui04)">
            <Button
              onClick={deletePositionsNumber}
              startIcon={<DeleteIcon sx={{ color: 'var(--text03)' }} />}
            >
              <Typography>
                {t('NUMBER_HEADER_POPOVER.DELETE_POSITIONS_NUMBER')}
              </Typography>
            </Button>
          </Box>
        )}
      </Box>
    </Popover>
  );
};
